import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OrganisationSelect } from '../components/OrganisationSelect';
import { FileUploadPanel } from '../components/FileUploadPanel';
import { PreviewEditor } from '../components/PreviewEditor';
import { ValidationErrors } from '../components/ValidationErrors';
import { ExecutionProgress } from '../components/ExecutionProgress';
import { ExecutionResult } from '../components/ExecutionResult';
import { ParseWarnings } from '../components/ParseWarnings';
import { useOnboardingState } from '../hooks/useOnboardingState';
import { parseExcelFile, validateOnboarding, executeOnboarding } from '../api';
import { EXECUTION_STEPS } from '../constants';
import type { PreviewData } from '../types';

export function CustomerOnboardingPage() {
  const { t } = useTranslation('customer-onboarding');
  const { state, actions } = useOnboardingState();
  const [parseError, setParseError] = useState<string | null>(null);
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Parse Excel file mutation
  const parseMutation = useMutation({
    mutationFn: parseExcelFile,
    onSuccess: (data) => {
      setParseError(null);
      // Auto-fill account name from the selected account when Info sheet is missing
      if (!data.info.organisationName && state.organisationName) {
        data = {
          ...data,
          info: { ...data.info, organisationName: state.organisationName },
        };
      }
      actions.parseSuccess(data);
      toast.success(t('toast.parseSuccess'));
      if (data.warnings && data.warnings.length > 0) {
        toast.warning(t('warnings.title', { count: data.warnings.length }));
      }
    },
    onError: (error: Error) => {
      const message = error.message || t('upload.errors.parseFailed');
      setParseError(message);
      toast.error(t('toast.parseFailed'));
    },
  });

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: validateOnboarding,
    onMutate: () => {
      actions.startValidation();
    },
    onSuccess: (response) => {
      // Backend returns 'status' not 'success'
      if (response.status) {
        actions.validationSuccess();
        toast.success(t('toast.validationSuccess'));
      } else {
        const errors = response.errors || [];
        actions.validationFailure(errors);
        toast.error(t('toast.validationFailed', { count: errors.length }));
      }
    },
    onError: (error: Error) => {
      actions.validationFailure([
        {
          entity: 'system',
          row: 0,
          column: '',
          error: error.message || t('validation.failed'),
          value: '',
        },
      ]);
      toast.error(t('toast.validationFailed', { count: 1 }));
    },
  });

  // Execute mutation
  const executeMutation = useMutation({
    mutationFn: executeOnboarding,
    onMutate: () => {
      actions.startExecution();
      // Only simulate progress for actual executions (dry run returns immediately)
      if (!dryRun) {
        startProgressSimulation();
      }
    },
    onSuccess: (response) => {
      stopProgressSimulation();
      // Backend returns 'status' not 'success'
      if (response.status) {
        actions.executionSuccess(response);
        toast.success(t('toast.executionSuccess'));
      } else {
        actions.executionFailure(response);
        toast.error(t('toast.executionFailed'));
      }
    },
    onError: (error: Error) => {
      stopProgressSimulation();
      actions.executionFailure({
        status: false,
        error: { message: error.message || t('result.failure.title') },
      });
      toast.error(t('toast.executionFailed'));
    },
  });

  // Start progress simulation with proper cleanup
  const startProgressSimulation = useCallback(() => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    let step = 0;
    progressIntervalRef.current = setInterval(() => {
      if (step < EXECUTION_STEPS.length - 1) {
        step++;
        actions.updateExecutionProgress(step, EXECUTION_STEPS[step]);
      } else {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
    }, 800);
  }, [actions]);

  // Stop progress simulation
  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Handlers
  const handleFileSelect = useCallback(
    (file: File) => {
      setParseError(null);
      actions.selectFile(file);
    },
    [actions]
  );

  const handleParse = useCallback(() => {
    if (state.file) {
      parseMutation.mutate(state.file);
    }
  }, [state.file, parseMutation]);

  const handlePreviewChange = useCallback(
    (data: PreviewData) => {
      actions.updatePreview(data);
    },
    [actions]
  );

  const handleValidate = useCallback(() => {
    if (state.previewData && state.organisationId) {
      validateMutation.mutate({
        parsed: state.previewData,
        organisation: state.organisationId,
      });
    }
  }, [state.previewData, state.organisationId, validateMutation]);

  const handleExecute = useCallback(() => {
    if (state.previewData && state.organisationId) {
      executeMutation.mutate({
        parsed: state.previewData,
        organisation: state.organisationId,
        options: {
          dryRun,
          sendWelcomeEmails,
        },
      });
    }
  }, [state.previewData, state.organisationId, executeMutation, dryRun, sendWelcomeEmails]);

  const handleBack = useCallback(() => {
    actions.backToPreview();
  }, [actions]);

  const handleRetry = useCallback(() => {
    actions.backToPreview();
  }, [actions]);

  const handleDryRunChange = useCallback((value: boolean) => {
    setDryRun(value);
    if (value) {
      setSendWelcomeEmails(false);
    }
  }, []);

  const handleStartOver = useCallback(() => {
    actions.reset();
    setParseError(null);
  }, [actions]);

  // Determine what to show based on current step
  const showFileUpload = ['select-org', 'upload'].includes(state.step);
  const showPreviewEditor = [
    'preview',
    'validating',
    'validated',
    'executing',
  ].includes(state.step);
  const showExecutionProgress = state.step === 'executing';
  const showExecutionResult = ['success', 'error'].includes(state.step);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('organisation.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <OrganisationSelect
            value={state.organisationId}
            onChange={actions.selectOrganisation}
            disabled={showPreviewEditor || showExecutionResult}
          />
        </CardContent>
      </Card>

      {showFileUpload && (
        <Card>
          <CardHeader>
            <CardTitle>{t('upload.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUploadPanel
              file={state.file}
              onFileSelect={handleFileSelect}
              onFileClear={actions.clearFile}
              onParse={handleParse}
              isParsing={parseMutation.isPending}
              disabled={!state.organisationId}
              error={parseError}
            />
          </CardContent>
        </Card>
      )}

      {/* Parse Warnings */}
      {state.parseWarnings.length > 0 && showPreviewEditor && (
        <ParseWarnings warnings={state.parseWarnings} />
      )}

      {showPreviewEditor && state.previewData && (
        <Card>
          <CardHeader>
            <CardTitle>{t('preview.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <PreviewEditor
              data={state.previewData}
              organisationName={state.organisationName || ''}
              onChange={handlePreviewChange}
              validationErrors={state.validationErrors}
              onValidate={handleValidate}
              onExecute={handleExecute}
              onBack={handleBack}
              isValidated={state.isValidated}
              isValidating={validateMutation.isPending}
              isExecuting={executeMutation.isPending}
              sendWelcomeEmails={sendWelcomeEmails}
              onSendWelcomeEmailsChange={setSendWelcomeEmails}
              dryRun={dryRun}
              onDryRunChange={handleDryRunChange}
            />
          </CardContent>
        </Card>
      )}

      {state.validationErrors.length > 0 && !showExecutionResult && (
        <ValidationErrors errors={state.validationErrors} />
      )}

      {showExecutionProgress && state.executionProgress && (
        <ExecutionProgress
          currentStep={state.executionProgress.currentStep}
          stepLabel={state.executionProgress.stepLabel}
        />
      )}

      {showExecutionResult && state.executionResult && (
        <ExecutionResult
          result={state.executionResult}
          onRetry={handleRetry}
          onStartOver={handleStartOver}
          canRetry={!!state.previewData}
        />
      )}
    </div>
  );
}
